import './LeaderboardBackToOGV2.css';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react"
import { useLocation } from 'react-router-dom';
import { enrichWithPreviousLeaderboard, fetchUnifiedLeaderboardData, parseExcludedSessionIds } from '../../shared/leaderboardShared';
import { fetchDemoLeaderboardData } from '../back-to-og/backToOgDemo';

const TEAMS_PER_PAGE = 10;
const ROW_PITCH = 68.556;
const NAME_FONT_MAX = 35;
const NAME_FONT_MIN = 18;
const REFRESH_MS = 15000;
const DEMO_REFRESH_MS = 4000;
const DEFAULT_DATE = '24 octobre 2026';
const DEFAULT_ROTATE_SECONDS = 15;
const PAGE_TITLE = 'Leaderboard Back To OG';
const BLANK_ICON = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function isTrue(value) {
    return value === 'true' || value === '1';
}

function splitLastWord(text) {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return [words.join(' '), ''];
    return [words.slice(0, -1).join(' '), words[words.length - 1]];
}

function rankTone(rank) {
    if (rank === 1) return 'bog2_tone_pink';
    if (rank === 2) return 'bog2_tone_mint';
    if (rank === 3) return 'bog2_tone_cyan';
    return 'bog2_tone_base';
}

const Row = React.memo(function Row({ index, rank, displayName, points, elims, wins, onClick, refreshDelta, animationEnabled, cascadeFadeEnabled, alive, dimmed, showFlags, memberData, fontVersion }) {
    const nameRef = useRef(null);
    const [nameFit, setNameFit] = useState({ size: NAME_FONT_MAX, scaleX: 1 });
    const hasFlagMembers = showFlags && memberData && memberData.length > 0;

    useLayoutEffect(() => {
        const element = nameRef.current;
        const container = element?.parentElement;
        if (!element || !container) return;

        const available = container.clientWidth;
        const previousSize = element.style.fontSize;
        const previousTransform = element.style.transform;
        element.style.fontSize = `${NAME_FONT_MAX}px`;
        element.style.transform = 'none';
        const needed = element.getBoundingClientRect().width * 1.03;
        element.style.fontSize = previousSize;
        element.style.transform = previousTransform;

        if (available <= 0 || needed <= available) {
            setNameFit({ size: NAME_FONT_MAX, scaleX: 1 });
            return;
        }
        const size = Math.max(NAME_FONT_MIN, Math.floor(NAME_FONT_MAX * available / needed));
        const neededAtSize = needed * size / NAME_FONT_MAX;
        setNameFit({ size, scaleX: neededAtSize > available ? Math.max(0.5, available / neededAtSize) : 1 });
    }, [displayName, memberData, showFlags, fontVersion]);

    const getAnimationStyle = () => {
        if (!animationEnabled || !refreshDelta) return {};

        const distance = Math.abs(refreshDelta) * ROW_PITCH;
        const duration = Math.max(0.6, Math.min(2.0, distance / 200));

        return {
            '--slide-from': `${refreshDelta > 0 ? distance : -distance}px`,
            animation: `bog2SlideIn ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            zIndex: 10,
        };
    };

    const rowClassName = ['bog2_row', rankTone(rank), dimmed ? 'bog2_is_dead' : ''].filter(Boolean).join(' ');

    return (
        <div
            className={rowClassName}
            style={{
                top: `${index * ROW_PITCH}px`,
                ...(cascadeFadeEnabled ? {
                    opacity: 0,
                    animation: `bog2FadeIn 0.6s ${index * 0.07}s forwards`,
                } : getAnimationStyle()),
            }}
        >
            <div className='bog2_box bog2_box_rank'>
                <span>Top {rank}</span>
            </div>
            {alive && <span className='bog2_alive_dot' />}
            <div className='bog2_name' onClick={onClick}>
                <span
                    ref={nameRef}
                    className='bog2_name_text'
                    style={{
                        fontSize: `${nameFit.size}px`,
                        transform: nameFit.scaleX < 1 ? `scaleX(${nameFit.scaleX})` : undefined,
                    }}
                >
                    {hasFlagMembers ? (
                        memberData.map((member, memberIndex) => (
                            <span key={memberIndex} className='bog2_member'>
                                <img
                                    src={member.flagUrl}
                                    alt=''
                                    className='bog2_flag'
                                    onError={(event) => {
                                        event.target.onerror = null;
                                        event.target.src = member.fallbackFlagUrl;
                                    }}
                                />
                                <span>{member.name}</span>
                                {memberIndex < memberData.length - 1 && <span className='bog2_separator'>+</span>}
                            </span>
                        ))
                    ) : (
                        displayName
                    )}
                </span>
            </div>
            <div className='bog2_box bog2_box_stat bog2_box_wins'>
                <span>{wins}</span>
            </div>
            <div className='bog2_box bog2_box_stat bog2_box_kills'>
                <span>{elims}</span>
            </div>
            <div className='bog2_box bog2_box_points'>
                <span>{points}<small>pts</small></span>
            </div>
        </div>
    );
});

function LeaderboardBackToOGV2({ fetchLeaderboardData = fetchUnifiedLeaderboardData, defaultLeaderboardId = '', showLoadState = false }) {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const leaderboard_id = urlParams.get('id') || defaultLeaderboardId;
    const demoMode = isTrue(urlParams.get('demo'));
    const showFlags = isTrue(urlParams.get('flags'));
    const titleText = urlParams.get('title') || 'Classement';
    const [dateFirst, dateSecond] = splitLastWord(urlParams.get('date') ?? DEFAULT_DATE);
    const rotateParam = urlParams.get('rotate');
    const rotateSeconds = rotateParam === null || rotateParam === '' ? DEFAULT_ROTATE_SECONDS : Number.parseInt(rotateParam, 10);
    const excludedSessionIds = useMemo(() => parseExcludedSessionIds(new URLSearchParams(location.search)), [location.search]);
    const excludedSessionIdsKey = useMemo(() => Array.from(excludedSessionIds).sort().join(','), [excludedSessionIds]);

    const [leaderboard, setLeaderboard] = useState([]);
    const [teamDetails, setTeamDetails] = useState({});
    const [page, setPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [animationEnabled, setAnimationEnabled] = useState(false);
    const [cascadeFadeEnabled, setCascadeFadeEnabled] = useState(isTrue(urlParams.get('cascade')));
    const [fontVersion, setFontVersion] = useState(0);
    const [loadState, setLoadState] = useState('Chargement du classement…');
    const previousLeaderboardRef = useRef(null);
    const startedAtBottomRef = useRef(false);

    const loadLeaderboard = async () => {
        if (!demoMode && !leaderboard_id) return;
        try {
            const data = demoMode
                ? fetchDemoLeaderboardData()
                : await fetchLeaderboardData({
                    leaderboardId: leaderboard_id,
                    excludedSessionIds,
                    showFlags,
                    forceRankByPoints: true,
                    includeV7: true,
                    indicatorsOnlyWhenAllDead: true,
                });

            const previousData = previousLeaderboardRef.current;
            const merged = enrichWithPreviousLeaderboard(data.leaderboard, previousData);
            const previousPlaces = new Map((previousData || []).map((team) => [team.teamname, team.place]));
            const rows = merged.leaderboard.map((team) => {
                const previousPlace = previousPlaces.get(team.teamname);
                const members = data.teamDetails[team.teamname]?.members || [];
                const memberNames = members.map((member) => member.ingame_name || member.name).filter(Boolean);
                return {
                    ...team,
                    displayName: memberNames.length ? memberNames.join(' + ') : team.teamname,
                    refreshDelta: typeof previousPlace === 'number' ? previousPlace - team.place : 0,
                };
            });

            setLeaderboard(rows);
            setLoadState(rows.length ? '' : 'Aucun résultat pour le moment.');
            setTeamDetails(data.teamDetails);
            previousLeaderboardRef.current = rows;

            if (previousData && merged.changedCount > 0) {
                setAnimationEnabled(true);
                setTimeout(() => { setAnimationEnabled(false); }, 2500);
            } else {
                setAnimationEnabled(false);
            }
        } catch (error) {
            setLoadState('Actualisation indisponible. Nouvelle tentative dans 15 secondes.');
            console.error('Error loading leaderboard data:', error);
        }
    };

    useEffect(() => {
        const previousTitle = document.title;
        const icons = Array.from(document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']"));
        const previousHrefs = icons.map((icon) => icon.getAttribute('href'));
        document.title = PAGE_TITLE;
        icons.forEach((icon) => icon.setAttribute('href', BLANK_ICON));
        return () => {
            document.title = previousTitle;
            icons.forEach((icon, index) => icon.setAttribute('href', previousHrefs[index]));
        };
    }, []);

    useEffect(() => {
        const fonts = document.fonts;
        if (!fonts) return undefined;
        let active = true;
        const refit = () => {
            if (active) setFontVersion((current) => current + 1);
        };
        fonts.load(`${NAME_FONT_MAX}px "Obviously Black Italic"`).then(refit).catch(() => {});
        fonts.ready.then(refit);
        fonts.addEventListener?.('loadingdone', refit);
        return () => {
            active = false;
            fonts.removeEventListener?.('loadingdone', refit);
        };
    }, []);

    useEffect(() => {
        previousLeaderboardRef.current = null;
        loadLeaderboard();
        const interval = setInterval(loadLeaderboard, demoMode ? DEMO_REFRESH_MS : REFRESH_MS);
        return () => clearInterval(interval);
    }, [leaderboard_id, demoMode, showFlags, excludedSessionIdsKey, fetchLeaderboardData]);

    const filteredLeaderboard = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return leaderboard;
        return leaderboard.filter((team) => {
            if (team.teamname.toLowerCase().includes(query)) return true;
            if (!Number.isNaN(Number(query)) && team.place === Number(query)) return true;
            return (teamDetails[team.teamname]?.members || []).some((member) =>
                String(member.ingame_name || '').toLowerCase().includes(query)
            );
        });
    }, [leaderboard, teamDetails, searchQuery]);

    const pageCount = Math.max(1, Math.ceil(filteredLeaderboard.length / TEAMS_PER_PAGE));
    const currentPage = Math.min(page, pageCount - 1);

    const nextPage = () => setPage((current) => (current + 1) % pageCount);
    const previousPage = () => setPage((current) => (current - 1 + pageCount) % pageCount);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    useEffect(() => {
        if (startedAtBottomRef.current || leaderboard.length === 0) return;
        startedAtBottomRef.current = true;
        setPage(Math.max(0, Math.ceil(leaderboard.length / TEAMS_PER_PAGE) - 1));
    }, [leaderboard.length]);

    useEffect(() => {
        if (!Number.isFinite(rotateSeconds) || rotateSeconds <= 0 || pageCount <= 1) return undefined;
        const timeoutId = setTimeout(() => {
            setPage((current) => (current - 1 + pageCount) % pageCount);
        }, rotateSeconds * 1000);
        return () => clearTimeout(timeoutId);
    }, [rotateSeconds, pageCount, currentPage]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'F1') {
                event.preventDefault();
                setShowSearch((previous) => !previous);
            } else if (event.key === 'F8') {
                event.preventDefault();
                setCascadeFadeEnabled((previous) => !previous);
            } else if (event.key === 'ArrowRight' && event.target.tagName !== 'INPUT') {
                setPage((current) => (current + 1) % pageCount);
            } else if (event.key === 'ArrowLeft' && event.target.tagName !== 'INPUT') {
                setPage((current) => (current - 1 + pageCount) % pageCount);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [pageCount]);

    const pageStart = currentPage * TEAMS_PER_PAGE;
    const pageItems = filteredLeaderboard.slice(pageStart, pageStart + TEAMS_PER_PAGE);
    const hasAliveTeams = leaderboard.some((team) => team.alive);
    const selectedDetails = selectedTeam ? teamDetails[selectedTeam] : null;
    const selectedSessions = [...(selectedDetails?.sessions || [])]
        .sort((a, b) => (Number(a.start_time) || 0) - (Number(b.start_time) || 0));
    const finishedPlaces = selectedSessions.map((session) => session.place).filter((place) => place > 0);

    return (
        <div className='back_to_og_v2'>
            <div className='bog2_canvas'>
                {showSearch && (
                    <div className='bog2_search'>
                        <input
                            type='text'
                            className='bog2_search_input'
                            value={searchQuery}
                            placeholder='Rechercher un joueur'
                            onChange={(event) => setSearchQuery(event.target.value)}
                            autoFocus
                        />
                    </div>
                )}

                <div className='bog2_title'>{titleText}</div>

                {(dateFirst || dateSecond) && (
                    <div className='bog2_mini bog2_mini_date'>
                        <span>{dateFirst}</span>
                        {dateSecond && <span>{dateSecond}</span>}
                    </div>
                )}
                <div className='bog2_mini bog2_mini_left'>
                    <span>Back to OG</span>
                    <span>Inoxtag</span>
                </div>
                <div className='bog2_mini bog2_mini_right'>
                    <span>Fortnite</span>
                    <span>Back to OG</span>
                </div>

                <div className='bog2_header bog2_header_wins bog2_is_clickable' onClick={previousPage}>
                    <span>Victory</span>
                    <span>Royale</span>
                </div>
                <div className='bog2_header bog2_header_kills'>Kills</div>
                <div className='bog2_header bog2_header_points bog2_is_clickable' onClick={nextPage}>Points</div>

                <div className='bog2_rows'>
                    {showLoadState && loadState && <div className='bog2_load_state' role='status'>{loadState}</div>}
                    {pageItems.map((data, index) => (
                        <Row
                            key={`${data.teamId || data.teamname}-${data.place}`}
                            index={index}
                            rank={data.place}
                            displayName={data.displayName}
                            points={data.points}
                            elims={data.elims}
                            wins={data.wins}
                            onClick={() => setSelectedTeam(data.teamname)}
                            refreshDelta={data.refreshDelta || 0}
                            animationEnabled={animationEnabled && data.hasPositionChanged}
                            cascadeFadeEnabled={cascadeFadeEnabled}
                            alive={data.alive}
                            dimmed={hasAliveTeams && !data.alive}
                            showFlags={showFlags}
                            memberData={data.memberData}
                            fontVersion={fontVersion}
                        />
                    ))}
                </div>

                {selectedDetails && (
                    <div className='bog2_modal_overlay' onClick={() => setSelectedTeam(null)}>
                        <div className='bog2_modal' onClick={(event) => event.stopPropagation()}>
                            <button className='bog2_modal_close' onClick={() => setSelectedTeam(null)}>×</button>
                            <div className='bog2_modal_header'>
                                <span className='bog2_modal_rank'>Top {selectedDetails.teamData.place}</span>
                                <div className='bog2_modal_members'>
                                    {selectedDetails.members.map((member, index) => (
                                        <span key={index} className='bog2_modal_member'>{member.ingame_name || member.name}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='bog2_modal_stats'>
                                <div className='bog2_modal_stat'><span className='bog2_modal_value'>{selectedDetails.teamData.points}</span><span className='bog2_modal_label'>Points</span></div>
                                <div className='bog2_modal_stat'><span className='bog2_modal_value'>{selectedSessions.length}</span><span className='bog2_modal_label'>Games</span></div>
                                <div className='bog2_modal_stat'><span className='bog2_modal_value'>{selectedSessions.filter((session) => session.place === 1).length}</span><span className='bog2_modal_label'>Top 1</span></div>
                                <div className='bog2_modal_stat'><span className='bog2_modal_value'>{selectedSessions.reduce((sum, session) => sum + (Number(session.kills) || 0), 0)}</span><span className='bog2_modal_label'>Kills</span></div>
                                <div className='bog2_modal_stat'><span className='bog2_modal_value'>{finishedPlaces.length ? Math.min(...finishedPlaces) : '-'}</span><span className='bog2_modal_label'>Meilleure place</span></div>
                            </div>
                            <div className='bog2_sessions'>
                                <div className='bog2_session bog2_session_head'>
                                    <div>Game</div><div>Place</div><div>Kills</div>
                                </div>
                                {selectedSessions.map((session, index) => (
                                    <div key={index} className='bog2_session'>
                                        <div>{index + 1}</div>
                                        <div className={session.place > 0 ? (session.place === 1 ? 'bog2_place_win' : '') : 'bog2_place_live'}>
                                            {session.place > 0 ? session.place : 'En jeu'}
                                        </div>
                                        <div>{session.kills}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LeaderboardBackToOGV2
